import type { OpenAPI } from "simplydo/interfaces"

import { getLocalStorageItem } from "./helpers"

// Some global permissions are unscoped, meaning they are not tied to a specific organisation or challenge
// For example, viewing the superadmin dashboard supersedes the idea of an organisation, therefore the scope is irrelevant
// However, things like ghosting INTO an organisation are scoped, because you can only ghost into an organisation you have access to
// There are also some middle grounds, handled on a per-function basis. For example, listing recent users will only list users in organisations you have access to, but listing all users is a global permission
const UNSCOPED_GLOBAL_PERMISSIONS = [
  "super.viewDashboard",
  "super.listUsers",
  "super.createOrgs",
  "super.managePermissions",
  "super.listRecents",
  "super.listOrgs",
]

export const hasPermission = (
  user: OpenAPI.Schemas["User"] | undefined,
  permission: OpenAPI.Schemas["User"]["permissions"][string][number],
  scope?: string,
  subScope?: string | string[],
) => {
  if (!user || !user.permissions || !permission) return false
  // See comments above for how unscope permissions work
  if (UNSCOPED_GLOBAL_PERMISSIONS.indexOf(permission) > -1) {
    for (const [scopeKey, permissions] of Object.entries(user.permissions)) {
      if (scopeKey.includes("global") && scopeKey !== "global" && permissions.indexOf(permission) > -1) {
        return true
      }
    }
  }
  if (user.permissions.global) {
    for (let i = 0; i < user.permissions.global.length; i += 1) {
      if (permission === user.permissions.global[i]) return true
    }
  }
  if (scope) {
    const relevantRoles = [...(user.permissions[scope] || []), ...(user.permissions[`global.${scope}`] || [])]
    for (let i = 0; i < relevantRoles.length; i += 1) {
      if (permission === relevantRoles[i]) return true
    }
    if (subScope) {
      if (!Array.isArray(subScope)) {
        subScope = [subScope]
      }
      const permParts = permission.split(".")
      const subScopePermission = `${permParts[0]}-${scope}.${permParts.slice(1).join(".")}`
      for (const subScopeItem of subScope) {
        const subScopeRoles = [
          ...(user.permissions[subScopeItem] || []),
          ...(user.permissions[`global.${subScopeItem}`] || []),
        ]
        for (let i = 0; i < subScopeRoles.length; i += 1) {
          if (subScopePermission === subScopeRoles[i]) return true
        }
      }
    }
  }
  return false
}

export const canEditIdea = (
  user: OpenAPI.Schemas["User"] | undefined,
  idea: OpenAPI.GET<"/ideas/{id}">["response"] | null,
  needsAuth = false,
): boolean => {
  if (!idea) return false
  if (hasPermission(user, "org.manageChallenges", idea.ownerChallenge?.organisation)) {
    return true
  }
  if (hasPermission(user, "challenge.editIdeas", idea.challenge)) {
    return true
  }

  const canEdit =
    user &&
    idea &&
    (idea.user === user._id ||
      (idea.collaborators || []).indexOf(user._id) > -1 ||
      (idea.authors || []).find((author) => author._id === user._id))
  if (!canEdit && idea.authToken && !needsAuth) {
    const authToken = getLocalStorageItem(`ideaAuthToken:${idea._id}`)
    return authToken && idea.authToken === authToken
  }
  return !!canEdit
}

export const canManageChallenge = (
  user: OpenAPI.Schemas["User"] | undefined,
  challenge: OpenAPI.Schemas["Challenge"] | undefined,
) => {
  return user && challenge && hasPermission(user, "org.manageChallenges", challenge.organisation)
}

export const canManageGroups = (user: OpenAPI.Schemas["User"] | undefined, organisation: string) => {
  return (
    user &&
    organisation &&
    (hasPermission(user, "org.manageGroups", organisation) || hasPermission(user, "super.viewDashboard"))
  )
}
